import * as as from '@ontologies/as';
import { NamedNode, SomeTerm } from '@ontologies/core';
import * as rdfx from '@ontologies/rdf';
import * as schema from '@ontologies/schema';
import { SomeNode } from 'link-lib';
import {
  LaxNode,
  useDataFetching,
  useFields,
  useGlobalIds,
  useIds,
  useResourceLink,
} from 'link-redux';
import { SubjectProp } from 'link-redux/dist-types/types';
import React, {
  FC,
  ReactNode,
} from 'react';

import { isInvisibleActionStatus } from '../Action/hooks/useVisibleActions';
import ResourceBoundary from '../ResourceBoundary';
import { tryParseInt } from '../../lib/numbers';
import LinkLoader from '../LinkLoader';
import { useContainerToArr } from '../../hooks/useContainerToArr';
import ontola from '../../../ontology/ontola';

import { useCurrentCollectionResource } from './hooks/useCurrentCollectionResource';
import { useSorting } from './hooks/useSorting';
import { CollectionContext, collectionContext } from './CollectionContext';
import CollectionFrame from './Frame/CollectionFrame';

interface CollectionProps {
  collectionDisplay?: NamedNode;
  hidePagination?: boolean;
  redirectPagination?: boolean;
  renderWhenEmpty?: boolean;
  subject: SomeNode;
}

interface CollectionChildrenProps extends CollectionProps {
  children: ReactNode;
  renderer?: undefined;
}
interface CollectionRendererProps extends CollectionProps {
  children?: undefined;
  renderer: FC<SubjectProp>;
}

export type CollectionProviderProps = CollectionChildrenProps | CollectionRendererProps;

export interface CollectionDataProps {
  collectionDisplayFromData: NamedNode;
  maxColumns: SomeTerm;
  partOf: LaxNode;
  totalItems: SomeTerm;
}

const propMap = {
  collectionDisplayFromData: ontola.collectionDisplay,
  maxColumns: ontola.maxColumns,
  partOf: as.partOf,
  totalItems: as.totalItems,
};

export const useHasInteraction = (collectionResource: SomeNode): boolean => {
  const actions = useGlobalIds(collectionResource, ontola.createAction);
  useDataFetching(actions);
  const actionStatuses = useGlobalIds(actions, schema.actionStatus);

  const [collectionResourceType] = useGlobalIds(collectionResource, rdfx.type);

  if (collectionResourceType === ontola.SearchResult) {
    return true;
  }

  const validActionIndex = actionStatuses.flat().findIndex((actionStatus) => actionStatus && !isInvisibleActionStatus(actionStatus));

  return validActionIndex !== -1;
};

const CollectionProvider = ({
  children,
  collectionDisplay,
  hidePagination,
  redirectPagination,
  renderWhenEmpty,
  renderer,
  subject,
}: CollectionProviderProps): JSX.Element | null => {
  useDataFetching(subject);
  const {
    collectionDisplayFromData,
    maxColumns,
    partOf,
    totalItems,
  } = useResourceLink(subject, propMap) as CollectionDataProps;
  const originalCollection = partOf ?? subject;
  const [currentCollection, currentCollectionPages, setCollectionResource] = useCurrentCollectionResource(
    !!redirectPagination,
    subject,
  );
  const [firstPageItems] = useFields(currentCollectionPages[0], as.totalItems);

  const [columnSequence] = useIds(originalCollection, ontola.columns);
  const [columns] = useContainerToArr<NamedNode>(columnSequence);
  const resolvedCollectionDisplay = collectionDisplay ?? collectionDisplayFromData;

  const hasInteraction = useHasInteraction(originalCollection);
  const sortOptions = useSorting(currentCollection);
  const originalFilters = useFields(originalCollection, ontola.activeFilters);
  const activeFilters = useFields(currentCollection, ontola.activeFilters);
  const appliedFilters = activeFilters.filter((filter) => !originalFilters.includes(filter));

  const collectionOptions = React.useMemo<CollectionContext>(() => ({
    appliedFilters,
    collectionDisplay: resolvedCollectionDisplay,
    columns,
    currentCollection,
    currentCollectionPages,
    hasInteraction,
    hidePagination,
    maxColumns: tryParseInt(maxColumns),
    originalCollection,
    redirectPagination,
    setCollectionResource,
    sortOptions,
  }), [
    appliedFilters,
    columns,
    resolvedCollectionDisplay,
    currentCollection,
    currentCollectionPages,
    redirectPagination,
    hasInteraction,
    hidePagination,
    maxColumns,
    originalCollection,
    setCollectionResource,
    sortOptions,
  ]);

  if (!subject) {
    return <div data-test="no-subject" />;
  }

  if (tryParseInt(totalItems || firstPageItems) === 0 && appliedFilters.length === 0 && !renderWhenEmpty) {
    if (!hasInteraction) {
      return <div data-test="invalid-status" />;
    }
  }

  if (!currentCollection) {
    return <LinkLoader key="no-collection" />;
  }

  if (children) {
    return (
      <collectionContext.Provider value={collectionOptions}>
        <ResourceBoundary subject={currentCollection}>
          {children}
        </ResourceBoundary>
      </collectionContext.Provider>
    );
  }

  return (
    <collectionContext.Provider value={collectionOptions}>
      <ResourceBoundary subject={currentCollection}>
        <CollectionFrame renderer={renderer!} />
      </ResourceBoundary>
    </collectionContext.Provider>
  );
};

export default CollectionProvider;
